import { Link } from 'react-router-dom'
import {
  Lock,
  Unlock,
  Plus,
  ArrowRight,
  Trash2,
  Copy,
  Check,
  ShieldCheck,
} from 'lucide-react'
import { useState } from 'react'
import { useAppStore } from '../store'
import { api } from '../lib/api'
import { gradientStyle, groupVaultId, trackPointer } from '../lib/folderColors'
import { toast } from '../store/toast'
import { KeyCard, KeyCardStack } from '../components/vault/KeyCard'

export function HomePage() {
  const bookmarkedVaultIds = useAppStore((s) => s.bookmarkedVaultIds)
  const unlockedVault = useAppStore((s) => s.unlockedVault)
  const removeBookmarkedVault = useAppStore((s) => s.removeBookmarkedVault)
  const [removingId, setRemovingId] = useState<string | null>(null)
  const [copiedId, setCopiedId] = useState<string | null>(null)

  const handleRemove = async (vaultId: string) => {
    if (removingId) return
    setRemovingId(vaultId)
    try {
      await api.removeBookmark(vaultId)
      removeBookmarkedVault(vaultId)
      toast('Removed from your vaults. The vault itself still exists.')
    } catch (error) {
      console.error('Failed to remove bookmark:', error)
      toast('Could not remove that vault', 'error')
    } finally {
      setRemovingId(null)
    }
  }

  const handleCopy = async (vaultId: string) => {
    try {
      await navigator.clipboard.writeText(vaultId)
      setCopiedId(vaultId)
      setTimeout(() => setCopiedId(null), 1600)
    } catch {
      /* clipboard unavailable */
    }
  }

  const count = bookmarkedVaultIds.length

  return (
    <div className="space-y-10">
      <div className="flex flex-col gap-5 sm:flex-row sm:items-end sm:justify-between animate-fade-up">
        <div>
          <p className="mb-1 text-[13px] font-semibold text-muted">
            {count} {count === 1 ? 'vault' : 'vaults'}
          </p>
          <h1 className="font-display text-[34px] font-semibold leading-tight tracking-tight sm:text-[44px]">
            Your vaults
          </h1>
          <p className="mt-1 max-w-md text-muted">
            End-to-end encrypted bookmarks and notes. Only you can unlock them.
          </p>
        </div>
        <div className="flex gap-2">
          <Link to="/add" className="btn-secondary flex-1 sm:flex-none">
            Add existing
          </Link>
          <Link to="/create" className="btn-primary flex-1 sm:flex-none">
            <Plus className="h-4 w-4" />
            New vault
          </Link>
        </div>
      </div>

      {count === 0 ? (
        <div className="mx-auto max-w-[400px] pb-6 text-center">
          <KeyCardStack>
            <KeyCard
              vaultId="••••• ••••• •••••"
              dim
              footerLeft="Your first vault"
            />
          </KeyCardStack>
          <h2 className="mt-8 font-display text-xl font-semibold">
            No vaults yet
          </h2>
          <p className="mt-1.5 text-muted">
            Create a vault to start storing encrypted bookmarks and notes.
          </p>
          <Link to="/create" className="btn-primary mt-5">
            <Plus className="h-4 w-4" />
            Create a vault
          </Link>
        </div>
      ) : (
        <ul className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {bookmarkedVaultIds.map((vaultId, index) => {
            const isUnlocked = unlockedVault?.id === vaultId
            return (
              <li
                key={vaultId}
                className="animate-deal-in"
                style={{ animationDelay: `${80 + Math.min(index, 8) * 70}ms` }}
              >
                <div
                  className="gcard group relative flex h-[200px] flex-col justify-between rounded-[28px] p-5 transition-transform duration-500 ease-spring hover:-translate-y-1"
                  style={gradientStyle(vaultId)}
                  onPointerMove={trackPointer}
                >
                  <div className="flex items-start justify-between">
                    <span className="grid h-10 w-10 place-items-center rounded-[13px] bg-white/[0.16]">
                      {isUnlocked ? (
                        <Unlock className="h-5 w-5" />
                      ) : (
                        <Lock className="h-5 w-5" />
                      )}
                    </span>
                    <div className="relative z-10 flex gap-1 transition-opacity sm:opacity-0 sm:group-focus-within:opacity-100 sm:group-hover:opacity-100">
                      <button
                        type="button"
                        onClick={() => handleCopy(vaultId)}
                        className="grid h-10 w-10 place-items-center rounded-xl bg-black/15 transition-colors hover:bg-black/30"
                        aria-label={`Copy vault ID ${vaultId}`}
                      >
                        {copiedId === vaultId ? (
                          <Check className="h-4 w-4" />
                        ) : (
                          <Copy className="h-4 w-4" />
                        )}
                      </button>
                      <button
                        type="button"
                        onClick={() => handleRemove(vaultId)}
                        disabled={removingId === vaultId}
                        className="grid h-10 w-10 place-items-center rounded-xl bg-black/15 transition-colors hover:bg-black/30 disabled:opacity-50"
                        aria-label={`Remove vault ${vaultId} from this list`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>

                  <Link
                    to={`/v/${vaultId}`}
                    className="font-mono text-[19px] tracking-[0.12em] after:absolute after:inset-0 after:rounded-[28px] after:content-['']"
                  >
                    {groupVaultId(vaultId)}
                  </Link>

                  <div className="flex items-center justify-between text-[13.5px] text-white/[0.82]">
                    <span>
                      {isUnlocked
                        ? `${unlockedVault.data.items.length} items`
                        : 'Tap to unlock'}
                    </span>
                    <span
                      className={`inline-flex min-h-[28px] items-center gap-1.5 rounded-full px-3 text-[12.5px] font-bold ${
                        isUnlocked
                          ? 'bg-white text-[#140D33]'
                          : 'bg-white/[0.14]'
                      }`}
                    >
                      {isUnlocked ? 'Unlocked' : 'Locked'}
                      {!isUnlocked && (
                        <ArrowRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-0.5" />
                      )}
                    </span>
                  </div>
                </div>
              </li>
            )
          })}
        </ul>
      )}

      <div
        className="panel flex items-start gap-4 p-5 animate-fade-up"
        style={{ animationDelay: '300ms' }}
      >
        <span className="kind-ic note flex-none" aria-hidden="true">
          <ShieldCheck className="h-5 w-5" />
        </span>
        <div>
          <h2 className="font-display font-semibold">
            Zero-knowledge encryption
          </h2>
          <p className="mt-1 text-sm text-muted">
            Everything is encrypted with AES-256-GCM before it leaves your
            device. We never see your passphrase or your content — so if you
            lose your passphrase, the data can’t be recovered.
          </p>
        </div>
      </div>
    </div>
  )
}
